import React, { useState, useEffect } from 'react';
import { 
  Menu, X, ChevronRight, FileText, Download, Users, 
  Phone, Mail, MapPin, ArrowRight, Calendar, 
  MessageCircle, FileCheck, Layers, FileSignature, 
  Store, HeartHandshake, Briefcase, Sparkles, ShieldCheck,
  Building, Landmark, PlaneTakeoff, Sun
} from 'lucide-react';

export default function App() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isFloatingMenuOpen, setIsFloatingMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Efecto para cambiar el header al hacer scroll
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Paleta de colores principal (referencia para uso en clases de Tailwind)
  // Azul Profundo: #0A192F
  // Naranja Vibrante: #FF6B35
  // Amarillo/Dorado: #FFC107
  // Celeste (Complementario sutil): #00B4D8

  const primaryMenu = [
    { name: 'Inicio', id: '#inicio' },
    { name: 'Nosotros', id: '#nosotros' },
    { name: 'Trámites', id: '#tramites' },
    { name: 'Noticias', id: '#noticias' },
    { name: 'Transparencia', id: '#transparencia' },
  ];

  const secondaryMenu = [
    { name: 'Solicitudes', icon: <FileSignature size={18} />, href: '#tramites' },
    { name: 'Emprendedores', icon: <Store size={18} />, href: '#emprendedores' },
    { name: 'Formularios', icon: <FileText size={18} />, href: '#tramites' },
    { name: 'Actividades', icon: <Calendar size={18} />, href: '#noticias' },
    { name: 'Informes', icon: <FileCheck size={18} />, href: '#transparencia' },
  ];

  const noticias = [
    {
      id: 1,
      tag: "Comunidad",
      title: "Jornada Masiva de Limpieza y Recuperación en La Siesta",
      date: "25 Mayo, 2026",
      image: "https://images.unsplash.com/photo-1583521360098-b805408d6c70?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80",
      excerpt: "Vecinos, voluntarios y la Junta Comunal se unen para embellecer nuestras calles principales en una jornada enfocada en la sostenibilidad de Tocumen."
    },
    {
      id: 2,
      tag: "Educación",
      title: "Nuevos cursos técnicos para jóvenes del Sector Sur",
      date: "18 Mayo, 2026",
      image: "https://images.unsplash.com/photo-1531482615713-2afd69097998?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      excerpt: "La Junta Comunal abre inscripciones para talleres gratuitos de logística, inglés conversacional y atención al cliente."
    },
    {
      id: 3,
      tag: "Obras",
      title: "Avances en la pavimentación de rutas internas en Belén",
      date: "10 Mayo, 2026",
      image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      excerpt: "El proyecto de mejora vial avanza, facilitando el tránsito y mejorando la calidad de vida de cientos de familias."
    },
    {
      id: 4,
      tag: "Salud",
      title: "Feria de salud preventiva comunitaria en la Plaza Central",
      date: "02 Mayo, 2026",
      image: "https://images.unsplash.com/photo-1505751172876-fa1923c5c528?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      excerpt: "Más de 800 residentes se beneficiaron de atenciones médicas, vacunación y asesoría nutricional gratuita."
    }
  ];

  const tramites = [
    { id: 1, title: "Permisos Comunitarios", icon: <Briefcase size={26} />, desc: "Solicitudes formales para el uso de parques, canchas deportivas y actividades culturales." },
    { id: 2, title: "Apoyo Social", icon: <HeartHandshake size={26} />, desc: "Asistencia directa para familias vulnerables, casos médicos y mejoras habitacionales urgentes." },
    { id: 3, title: "Mediación Vecinal", icon: <Users size={26} />, desc: "Orientación gratuita y mediación comunitaria para resolver conflictos de convivencia pacíficamente." },
    { id: 4, title: "Certificaciones", icon: <ShieldCheck size={26} />, desc: "Emisión ágil de certificados de residencia y validación de trámites para los moradores." }
  ];

  const emprendedores = [
    { id: 1, name: "Asados El Paisano", community: "La Siesta", type: "Gastronomía", img: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" },
    { id: 2, name: "Taller Mecánico Hermanos", community: "Sector Sur", type: "Servicios", img: "https://images.unsplash.com/photo-1530046339160-ce3e530c7d2f?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" },
    { id: 3, name: "Mini Super La Bendición", community: "Belén", type: "Comercio", img: "https://images.unsplash.com/photo-1534723452862-4c874018d66d?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" },
    { id: 4, name: "Postres y Delicias", community: "Vía Principal", type: "Repostería", img: "https://images.unsplash.com/photo-1509440159596-0249088772ff?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" }
  ];

  const documentosTransparencia = [
    { id: 1, title: "Presupuesto de Inversión 2026", type: "PDF", size: "2.8 MB" },
    { id: 2, title: "Planilla de Funcionarios - Q1", type: "Excel", size: "1.3 MB" },
    { id: 3, title: "Informe de Obras Públicas", type: "PDF", size: "4.1 MB" },
    { id: 4, title: "Actas de Reuniones Comunitarias", type: "PDF", size: "3.5 MB" },
    { id: 5, title: "Solicitudes de Apoyo Atendidas", type: "Excel", size: "950 KB" },
    { id: 6, title: "Auditoría Interna 2025", type: "PDF", size: "5.2 MB" },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-sans text-slate-800 overflow-x-hidden selection:bg-[#FFC107] selection:text-[#0A192F] scroll-smooth">
      <style dangerouslySetInnerHTML={{__html: `
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        
        /* Glassmorphism Refinado - Adaptado a nueva paleta */
        .glass-modern { 
          background: rgba(255, 255, 255, 0.55); 
          backdrop-filter: blur(24px); 
          -webkit-backdrop-filter: blur(24px); 
          border: 1px solid rgba(255, 255, 255, 0.7); 
          box-shadow: 0 8px 32px rgba(10, 25, 47, 0.08);
        }
        .glass-dark-modern { 
          background: rgba(10, 25, 47, 0.75); 
          backdrop-filter: blur(24px); 
          -webkit-backdrop-filter: blur(24px); 
          border-bottom: 1px solid rgba(255, 255, 255, 0.08); 
        }
        .glass-banner {
          background: rgba(10, 25, 47, 0.6);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.15);
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
        }
        
        /* Franjas / Stripes Effect */
        .stripes-pattern {
          background-image: repeating-linear-gradient(
            45deg,
            rgba(255, 255, 255, 0.04) 0px,
            rgba(255, 255, 255, 0.04) 2px,
            transparent 2px,
            transparent 8px
          );
        }
        .stripes-dark {
          background-image: repeating-linear-gradient(
            -45deg,
            rgba(0, 0, 0, 0.02) 0px,
            rgba(0, 0, 0, 0.02) 2px,
            transparent 2px,
            transparent 8px
          );
        }

        /* Elevaciones Fluidas */
        .card-elevate {
          transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
        }
        .card-elevate:hover {
          transform: translateY(-8px);
          box-shadow: 0 24px 48px rgba(10, 25, 47, 0.1), 0 4px 12px rgba(255, 107, 53, 0.08);
        }

        /* Micro-Animaciones */
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(2deg); }
        }
        .animate-float { animation: float 8s ease-in-out infinite; }
        
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        .animate-glow { animation: pulse-glow 6s ease-in-out infinite; }
        
        /* FAB Spring Animation */
        .fab-item {
          transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          opacity: 0;
          transform: translateY(20px) scale(0.8);
        }
        .fab-open .fab-item {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
        .fab-item:nth-child(1) { transition-delay: 0.05s; }
        .fab-item:nth-child(2) { transition-delay: 0.10s; }
        .fab-item:nth-child(3) { transition-delay: 0.15s; }
        .fab-item:nth-child(4) { transition-delay: 0.20s; }
        .fab-item:nth-child(5) { transition-delay: 0.25s; }
      `}} />

      {/* Franja superior institucional (Azul, Naranja, Amarillo) */}
      <div className="h-2 w-full flex relative z-50">
        <div className="w-1/3 bg-[#0A192F] stripes-pattern"></div>
        <div className="w-1/3 bg-[#FF6B35] stripes-pattern"></div>
        <div className="w-1/3 bg-[#FFC107] stripes-pattern"></div>
      </div>

      {/* Header Fijo */}
      <header className={`fixed w-full z-40 transition-all duration-500 ${scrolled ? 'top-0 glass-dark-modern shadow-[0_10px_30px_rgba(0,0,0,0.2)] py-1' : 'top-2 bg-transparent py-4'} text-white`}>
        <div className="absolute inset-0 stripes-pattern opacity-30 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex justify-between items-center h-16">
            
            {/* Logo Generado con CSS/Iconos - Estilo Tocumen */}
            <div className="flex-shrink-0 flex items-center gap-3 cursor-pointer group">
              <div className="h-12 w-12 rounded-[1rem] overflow-hidden border-2 border-[#FFC107]/80 shadow-[0_0_15px_rgba(255,193,7,0.3)] group-hover:scale-105 transition-all duration-300 bg-gradient-to-br from-[#0A192F] to-[#0f2d5c] flex items-center justify-center relative">
                <div className="absolute inset-0 bg-[#FF6B35] opacity-20 mix-blend-overlay"></div>
                {/* Combinación de iconos: Edificio y Avión (por el aeropuerto de Tocumen) */}
                <Building size={20} className="text-[#FFC107] relative z-10 absolute bottom-2 left-2 opacity-80" />
                <PlaneTakeoff size={22} className="text-white relative z-20 absolute top-2 right-1.5 drop-shadow-md group-hover:animate-pulse group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </div>
              <div className="flex flex-col justify-center">
                <span className="font-bold text-[11px] lg:text-xs leading-none uppercase tracking-widest text-[#00B4D8] mb-0.5">Junta Comunal</span>
                <span className="font-extrabold text-xl lg:text-2xl leading-none text-white tracking-wide drop-shadow-sm">TOCUMEN</span>
              </div>
            </div>

            {/* Menu Desktop */}
            <nav className="hidden lg:flex space-x-8 items-center">
              {primaryMenu.map((item) => (
                <a key={item.name} href={item.id} className="text-sm font-semibold hover:text-[#FFC107] transition-colors relative group tracking-wide">
                  {item.name}
                  <span className="absolute -bottom-1.5 left-0 w-0 h-0.5 bg-gradient-to-r from-[#FF6B35] to-[#FFC107] transition-all duration-300 group-hover:w-full rounded-full shadow-[0_0_8px_rgba(255,107,53,0.6)]"></span>
                </a>
              ))}
              <a href="#contacto" className="ml-4 px-7 py-2.5 rounded-full bg-gradient-to-r from-[#FF6B35] to-[#E85D2A] hover:from-[#FF7A4A] hover:to-[#FF6B35] border border-white/10 text-sm font-bold transition-all duration-300 flex items-center gap-2 shadow-[0_4px_15px_rgba(255,107,53,0.3)] hover:shadow-[0_6px_20px_rgba(255,107,53,0.5)]">
                Contáctenos
              </a>
            </nav>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center">
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-[#FFC107] hover:text-white transition-colors bg-white/10 p-2 rounded-lg backdrop-blur-md border border-white/20">
                {isMobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Panel */}
        {isMobileMenuOpen && (
          <div className="lg:hidden glass-dark-modern border-t border-white/10 absolute w-full left-0 shadow-2xl mt-1">
            <div className="px-4 pt-4 pb-6 space-y-3 relative z-10">
              {primaryMenu.map((item) => (
                <a key={item.name} href={item.id} onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-3 text-base font-semibold text-white hover:bg-white/10 hover:text-[#FFC107] rounded-xl transition-colors border border-transparent hover:border-white/10">
                  {item.name}
                </a>
              ))}
              <a href="#contacto" onClick={() => setIsMobileMenuOpen(false)} className="block w-full text-center mt-4 px-4 py-3 rounded-xl bg-gradient-to-r from-[#FF6B35] to-[#E85D2A] text-white font-bold shadow-lg border border-[#FF6B35]/30">
                Contáctenos
              </a>
            </div>
          </div>
        )}
      </header>

      {/* Floating Secondary Menu (FAB) Premium */}
      <div className="fixed bottom-6 right-6 lg:bottom-8 lg:right-8 z-50 flex flex-col items-end">
        <div className={`flex flex-col gap-3 mb-4 origin-bottom-right transition-all duration-300 ${isFloatingMenuOpen ? 'fab-open pointer-events-auto opacity-100 scale-100 translate-y-0' : 'pointer-events-none opacity-0 scale-95 translate-y-4'}`}>
          <div className="glass-modern p-3.5 rounded-3xl shadow-[0_15px_40px_rgba(10,25,47,0.15)] border border-white/80 flex flex-col gap-2">
            {secondaryMenu.map((item, index) => (
              <a key={index} href={item.href} onClick={() => setIsFloatingMenuOpen(false)} className="fab-item flex items-center gap-3.5 bg-transparent text-[#0A192F] px-5 py-3 rounded-2xl hover:bg-[#F8FAFC] hover:text-[#FF6B35] transition-all font-bold text-sm group border border-transparent hover:border-slate-200">
                <span className="text-[#FF6B35] group-hover:text-[#FFC107] group-hover:scale-110 transition-transform bg-slate-100 group-hover:bg-[#FFC107]/10 p-2 rounded-xl shadow-sm">
                  {item.icon}
                </span>
                {item.name}
              </a>
            ))}
          </div>
        </div>
        
        <button 
          onClick={() => setIsFloatingMenuOpen(!isFloatingMenuOpen)}
          className={`h-16 w-16 rounded-full flex items-center justify-center text-white shadow-[0_10px_25px_rgba(10,25,47,0.3)] transition-all duration-500 z-50 border border-white/20 ${isFloatingMenuOpen ? 'bg-gradient-to-br from-[#FF6B35] to-[#D45424] rotate-45 scale-105 shadow-[0_15px_35px_rgba(255,107,53,0.4)]' : 'bg-gradient-to-br from-[#0A192F] to-[#143058] hover:scale-110 hover:shadow-[0_15px_35px_rgba(10,25,47,0.5)]'}`}
        >
          {isFloatingMenuOpen ? <X size={26} /> : <Layers size={26} />}
        </button>
      </div>

      {/* Hero Section Refinada con Banner Transparente */}
      <section id="inicio" className="relative pt-32 pb-24 lg:pt-48 lg:pb-40 overflow-hidden bg-[#0A192F]">
        {/* Background Image (Ciudad/Comunidad) */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1542159187-5735c0de37f0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" 
            alt="Comunidad Tocumen" 
            className="w-full h-full object-cover opacity-70"
          />
          {/* Overlay suave para asegurar contraste general */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A192F]/60 via-transparent to-[#0A192F]/80"></div>
        </div>

        {/* Efectos de Iluminación Volumétrica (Blurs) */}
        <div className="absolute top-1/4 -left-20 w-[35rem] h-[35rem] bg-[#00B4D8] rounded-full mix-blend-screen filter blur-[130px] opacity-30 animate-glow pointer-events-none"></div>
        <div className="absolute bottom-0 right-10 w-[40rem] h-[40rem] bg-[#FF6B35] rounded-full mix-blend-screen filter blur-[150px] opacity-40 animate-float pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10 flex flex-col justify-center min-h-[50vh]">
          
          {/* BANNER TRANSPARENTE SOLICITADO */}
          <div className="glass-banner max-w-3xl p-8 md:p-12 rounded-[2.5rem] relative overflow-hidden group">
            {/* Brillo interno decorativo */}
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/10 to-transparent opacity-50 pointer-events-none"></div>
            <div className="absolute -inset-1 bg-gradient-to-r from-[#FF6B35]/20 to-[#FFC107]/20 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-1000 z-0"></div>

            <div className="relative z-10">
              <span className="inline-flex items-center gap-2 py-1.5 px-4 rounded-full bg-black/30 border border-white/20 text-xs sm:text-sm font-bold tracking-widest text-[#FFC107] mb-6 shadow-inner backdrop-blur-md">
                <Sun size={16} className="text-[#FF6B35]" />
                PUERTA DE PANAMÁ
              </span>
              
              <h1 className="text-4xl md:text-6xl lg:text-[4rem] font-extrabold tracking-tight mb-5 leading-[1.1] text-white drop-shadow-lg">
                Trabajando juntos <br/> por un <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FFC107] via-[#FFF1AB] to-[#FF6B35] drop-shadow-sm">Tocumen</span> mejor.
              </h1>
              
              <p className="text-base md:text-xl text-slate-200 mb-8 max-w-2xl font-light leading-relaxed drop-shadow-md">
                Impulsamos el desarrollo integral, la seguridad y el crecimiento económico de nuestro corregimiento, conectando a nuestra gente con nuevas oportunidades.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#tramites" className="bg-[#FF6B35] hover:bg-[#E85D2A] text-white px-8 py-4 rounded-2xl font-bold transition-all shadow-[0_8px_25px_rgba(255,107,53,0.4)] hover:shadow-[0_12px_35px_rgba(255,107,53,0.6)] hover:-translate-y-1 flex items-center justify-center gap-2 text-lg border border-[#FF6B35]/50">
                  Realizar Trámite <ArrowRight size={20} />
                </a>
                <a href="#noticias" className="bg-white/10 hover:bg-white/20 backdrop-blur-md text-white border border-white/30 px-8 py-4 rounded-2xl font-bold transition-all flex items-center justify-center gap-2 text-lg hover:-translate-y-1 shadow-lg">
                  Ver Noticias
                </a>
              </div>
            </div>
          </div>

        </div>
        
        {/* Franja decorativa inferior suave con wave adaptado a colores claros */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-20">
          <svg className="relative block w-full h-12 md:h-20" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M1200 120L0 16.48 0 0 1200 0 1200 120z" className="fill-[#0A192F]/10"></path>
            <path d="M0 120L1200 120 1200 0 0 120z" className="fill-[#F8FAFC]"></path>
          </svg>
        </div>
      </section>

      {/* Sección Nosotros / Representante */}
      <section id="nosotros" className="py-24 md:py-32 relative bg-[#F8FAFC] overflow-hidden">
        {/* Franjas decorativas sutiles */}
        <div className="absolute top-0 right-0 w-1/3 h-full stripes-dark opacity-30 pointer-events-none"></div>
        <div className="absolute -left-40 top-40 w-96 h-96 bg-[#FFC107] rounded-full filter blur-[150px] opacity-[0.07] pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            
            {/* Foto Representante (Estilo Premium con colores nuevos) */}
            <div className="lg:w-5/12 relative w-full max-w-md mx-auto lg:mx-0">
              <div className="absolute -top-5 -left-5 w-full h-full border-[3px] border-[#FFC107]/40 rounded-[2.5rem] z-0"></div>
              <div className="absolute -bottom-6 -right-6 w-3/4 h-3/4 bg-gradient-to-br from-[#0A192F] to-[#FF6B35] rounded-[2.5rem] z-0 opacity-15 animate-float filter blur-[12px]"></div>
              
              <div className="relative z-10 rounded-[2.5rem] overflow-hidden shadow-[0_25px_60px_rgba(10,25,47,0.15)] aspect-[4/5] bg-slate-200 group border-4 border-white">
                <img 
                  src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                  alt="H.R. Arielis Joana Barría" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-[#0A192F]/30 to-transparent opacity-90 mix-blend-multiply"></div>
                <div className="absolute bottom-0 left-0 w-full p-8 pt-20 bg-gradient-to-t from-[#0A192F] to-transparent">
                  <h3 className="text-3xl font-extrabold text-white mb-1 drop-shadow-md">H.R. Arielis J. Barría</h3>
                  <p className="text-[#FFC107] font-bold tracking-wide uppercase text-sm flex items-center gap-2 drop-shadow">
                    <ShieldCheck size={16} /> Representante de Tocumen
                  </p>
                </div>
              </div>
            </div>

            {/* Info y Contacto */}
            <div className="lg:w-7/12 relative">
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full glass-modern text-[#FF6B35] font-bold text-sm mb-6 border border-[#FF6B35]/20 shadow-sm">
                <Users size={16} /> Nuestra Gestión
              </div>
              <h2 className="text-4xl md:text-5xl font-extrabold text-[#0A192F] mb-6 leading-tight drop-shadow-sm">
                Compromiso firme con el desarrollo de nuestra gente.
              </h2>
              <p className="text-slate-600 mb-10 text-lg leading-relaxed font-medium">
                La Junta Comunal de Tocumen trabaja incansablemente para garantizar el bienestar de sus residentes, mejorar la infraestructura local, impulsar la educación y fomentar un entorno seguro y próspero en uno de los corregimientos más pujantes del país.
              </p>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="glass-modern p-6 rounded-3xl border border-white shadow-lg card-elevate flex items-start gap-5 group cursor-default">
                  <div className="bg-[#0A192F]/10 p-4 rounded-2xl text-[#0A192F] group-hover:bg-[#0A192F] group-hover:text-white transition-all shadow-inner">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800 text-sm uppercase tracking-wider mb-1">Teléfonos</h4>
                    <p className="text-[#0A192F] font-extrabold text-lg">295-0000 <br/> 295-0001</p>
                  </div>
                </div>
                <div className="glass-modern p-6 rounded-3xl border border-white shadow-lg card-elevate flex items-start gap-5 group cursor-default">
                  <div className="bg-[#FF6B35]/10 p-4 rounded-2xl text-[#FF6B35] group-hover:bg-[#FF6B35] group-hover:text-white transition-all shadow-inner">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800 text-sm uppercase tracking-wider mb-1">Correo</h4>
                    <p className="text-[#FF6B35] font-bold break-all leading-tight">info@jctocumen.gob.pa</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECCIÓN: Solicitudes y Trámites */}
      <section id="tramites" className="py-24 md:py-32 relative bg-white overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-slate-200 to-transparent"></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#FFC107]/10 via-transparent to-transparent opacity-70 pointer-events-none filter blur-[60px]"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full bg-[#0A192F]/5 text-[#0A192F] font-bold text-sm mb-4 border border-[#0A192F]/10 backdrop-blur-sm shadow-sm">
              <FileSignature size={16} className="text-[#FF6B35]" /> Servicios Comunitarios
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#0A192F] mb-6 drop-shadow-sm">Solicitudes y Trámites</h2>
            <p className="text-slate-600 text-lg font-medium">
              Gestiona tus permisos, ayudas sociales y certificaciones de forma rápida y transparente con tu Junta Comunal.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {tramites.map((tramite) => (
              <div key={tramite.id} className="bg-[#F8FAFC] border border-slate-100 rounded-[2rem] p-8 card-elevate group cursor-pointer relative overflow-hidden flex flex-col h-full shadow-[0_10px_30px_rgba(0,0,0,0.02)]">
                {/* Degradado/Franja decorativa on hover */}
                <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-br from-[#FFC107]/5 to-[#FF6B35]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 stripes-pattern"></div>
                
                <div className="relative h-16 w-16 bg-white shadow-sm rounded-2xl flex items-center justify-center text-[#0A192F] mb-6 group-hover:bg-[#0A192F] group-hover:text-[#FFC107] group-hover:shadow-[0_8px_20px_rgba(10,25,47,0.2)] transition-all duration-300 border border-slate-200 z-10">
                  {tramite.icon}
                </div>
                <h3 className="relative text-xl font-bold text-[#0A192F] mb-3 leading-tight z-10">{tramite.title}</h3>
                <p className="relative text-slate-600 text-sm mb-8 leading-relaxed flex-grow z-10 font-medium">{tramite.desc}</p>
                
                <div className="relative mt-auto flex items-center text-[#FF6B35] font-bold text-sm group-hover:text-[#D45424] z-10 bg-white/60 w-fit px-4 py-2 rounded-xl border border-transparent group-hover:border-orange-100">
                  Iniciar trámite <ArrowRight size={16} className="ml-2 transform group-hover:translate-x-1.5 transition-transform" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECCIÓN: Emprendedores */}
      <section id="emprendedores" className="py-24 md:py-32 relative bg-[#0A192F] text-white overflow-hidden border-t-[6px] border-[#FF6B35]">
        {/* Fondo oscuro con textura sutil */}
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1556761175-5973dc0f32d7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80')] mix-blend-overlay opacity-[0.08] object-cover"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-transparent to-[#0A192F]"></div>
        <div className="absolute inset-0 stripes-pattern opacity-20"></div>
        
        {/* Glows naranjas/amarillos */}
        <div className="absolute top-1/2 left-0 w-96 h-96 bg-[#FF6B35] rounded-full filter blur-[180px] opacity-15 pointer-events-none -translate-x-1/2"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-[#FFC107] rounded-full filter blur-[150px] opacity-10 pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row justify-between items-end mb-16 gap-8">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full glass-modern !bg-white/10 text-[#FFC107] font-bold text-sm mb-4 border border-white/15 shadow-sm backdrop-blur-md">
                <Store size={16} /> Economía Local
              </div>
              <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4 drop-shadow-md">Talento de Tocumen</h2>
              <p className="text-slate-300 text-lg font-light">Impulsamos la economía de nuestros barrios. Conoce y apoya a los comercios y emprendedores que hacen grande a nuestro corregimiento.</p>
            </div>
            <button className="bg-[#FFC107] hover:bg-[#F0B400] text-[#0A192F] px-8 py-4 rounded-2xl font-bold transition-all shadow-[0_8px_25px_rgba(255,193,7,0.2)] hover:shadow-[0_12px_35px_rgba(255,193,7,0.4)] hover:-translate-y-1 whitespace-nowrap flex items-center gap-2 text-lg border border-yellow-200">
              Registrar mi negocio <ArrowRight size={20} />
            </button>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {emprendedores.map((emp) => (
              <div key={emp.id} className="glass-dark-modern rounded-[2rem] overflow-hidden group cursor-pointer hover:-translate-y-2 transition-all duration-400 border border-white/10 hover:border-[#FF6B35]/50 shadow-[0_15px_30px_rgba(0,0,0,0.4)] relative">
                {/* Highlight superior on hover */}
                <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-[#FFC107] to-[#FF6B35] opacity-0 group-hover:opacity-100 transition-opacity z-20"></div>
                
                <div className="h-56 overflow-hidden relative">
                  <div className="absolute top-4 right-4 z-10 glass-modern !bg-black/50 text-[#FFC107] text-xs font-bold px-3 py-1.5 rounded-full border border-white/10 shadow-sm backdrop-blur-md">
                    {emp.type}
                  </div>
                  <img src={emp.img} alt={emp.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-[#0A192F]/50 to-transparent opacity-95 mix-blend-multiply"></div>
                </div>
                <div className="p-6 relative -mt-6 bg-gradient-to-b from-transparent to-[#0A192F]/80">
                  <h3 className="font-bold text-xl text-white mb-2 group-hover:text-[#FF6B35] transition-colors drop-shadow">{emp.name}</h3>
                  <div className="flex items-center gap-2 text-slate-300 text-sm font-medium">
                    <MapPin size={16} className="text-[#FFC107]" /> {emp.community}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sección Noticias */}
      <section id="noticias" className="py-24 md:py-32 bg-[#F8FAFC] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-6 bg-gradient-to-b from-[#0A192F]/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full glass-modern text-[#0A192F] font-bold text-sm mb-4 border border-[#0A192F]/10 shadow-sm">
                <MessageCircle size={16} className="text-[#FF6B35]" /> Entérate
              </div>
              <h2 className="text-4xl md:text-5xl font-extrabold text-[#0A192F] drop-shadow-sm">Noticias de la Comunidad</h2>
            </div>
            <button className="bg-transparent border-2 border-[#0A192F] text-[#0A192F] px-8 py-4 rounded-2xl font-bold hover:bg-[#0A192F] hover:text-white transition-all shadow-[0_5px_15px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_25px_rgba(10,25,47,0.2)] flex items-center gap-2 group text-lg">
              Ver todas <ArrowRight size={20} className="group-hover:translate-x-1.5 transition-transform" />
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Noticia Destacada */}
            <div className="lg:col-span-7 group cursor-pointer card-elevate">
              <div className="relative rounded-[2.5rem] overflow-hidden shadow-[0_15px_40px_rgba(0,0,0,0.08)] h-full min-h-[480px] border-4 border-white">
                <img src={noticias[0].image} alt={noticias[0].title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-[#0A192F]/40 to-transparent opacity-95 mix-blend-multiply"></div>
                
                <div className="absolute top-8 left-8 z-10 glass-modern !bg-white/20 text-white text-xs font-extrabold uppercase tracking-widest px-5 py-2 rounded-full shadow-lg border border-white/30 backdrop-blur-md">
                  {noticias[0].tag}
                </div>
                
                <div className="absolute bottom-0 left-0 w-full p-8 md:p-12">
                  <p className="text-[#FFC107] font-bold text-sm mb-4 flex items-center gap-2 glass-modern !bg-black/40 w-fit px-4 py-2 rounded-full border border-white/10 backdrop-blur-md">
                    <Calendar size={14} /> {noticias[0].date}
                  </p>
                  <h3 className="font-extrabold text-3xl md:text-4xl text-white mb-5 leading-tight group-hover:text-[#FFC107] transition-colors drop-shadow-lg">{noticias[0].title}</h3>
                  <p className="text-slate-200 text-base md:text-lg mb-0 line-clamp-3 md:line-clamp-none font-medium leading-relaxed drop-shadow">{noticias[0].excerpt}</p>
                </div>
              </div>
            </div>

            {/* Cuadrícula de otras noticias */}
            <div className="lg:col-span-5 flex flex-col gap-6">
              {noticias.slice(1).map((noticia) => (
                <div key={noticia.id} className="glass-modern !bg-white rounded-3xl overflow-hidden shadow-[0_8px_25px_rgba(0,0,0,0.04)] border border-slate-100 card-elevate group flex flex-col sm:flex-row h-full cursor-pointer relative">
                  {/* Hover accent line naranja */}
                  <div className="absolute left-0 top-0 w-1.5 h-full bg-[#FF6B35] scale-y-0 group-hover:scale-y-100 transition-transform origin-top z-20"></div>

                  <div className="sm:w-2/5 relative overflow-hidden min-h-[180px]">
                    <div className="absolute top-3 left-3 z-10 bg-white/90 backdrop-blur-sm text-[#0A192F] text-[10px] font-extrabold uppercase px-3 py-1 rounded-full shadow-sm">
                      {noticia.tag}
                    </div>
                    <img src={noticia.image} alt={noticia.title} className="w-full h-full object-cover absolute inset-0 group-hover:scale-110 transition-transform duration-700" />
                  </div>
                  <div className="p-6 sm:w-3/5 flex flex-col justify-center">
                    <p className="text-xs text-[#FF6B35] font-bold mb-3 flex items-center gap-1.5"><Calendar size={14}/> {noticia.date}</p>
                    <h3 className="font-bold text-lg text-[#0A192F] mb-3 line-clamp-2 group-hover:text-[#FF6B35] transition-colors leading-snug drop-shadow-sm">{noticia.title}</h3>
                    <p className="text-slate-500 text-sm line-clamp-2 mb-4 leading-relaxed font-medium">{noticia.excerpt}</p>
                    <span className="text-[#0A192F] font-bold text-sm inline-flex items-center gap-1.5 group-hover:text-[#FFC107] transition-colors mt-auto uppercase tracking-wide">
                      Leer más <ArrowRight size={16} className="transform group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sección Transparencia */}
      <section id="transparencia" className="py-24 md:py-32 relative bg-[#0A192F] overflow-hidden">
        {/* Patrón premium */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(#ffffff 2px, transparent 2px)', backgroundSize: '40px 40px' }}></div>
        <div className="absolute inset-0 stripes-pattern opacity-10"></div>
        <div className="absolute -top-40 right-20 w-[40rem] h-[40rem] bg-[#00B4D8] rounded-full filter blur-[200px] opacity-[0.15]"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full glass-modern !bg-white/10 text-white font-bold text-sm mb-4 border border-white/10 backdrop-blur-md shadow-sm">
              <ShieldCheck size={16} className="text-[#FFC107]" /> Gestión Abierta
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6 drop-shadow-md">Portal de Transparencia</h2>
            <p className="text-slate-300 text-lg font-light">
              Nuestro compromiso es con la honestidad. Accede libremente a los documentos financieros, informes y presupuestos de la Junta Comunal de Tocumen.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {documentosTransparencia.map((doc) => (
              <div key={doc.id} className="glass-dark-modern rounded-[2rem] p-6 hover:bg-white/10 transition-all duration-400 group cursor-pointer border border-white/5 hover:border-[#FFC107]/30 shadow-[0_10px_30px_rgba(0,0,0,0.3)] hover:-translate-y-1.5 relative overflow-hidden">
                <div className="flex items-start gap-5 relative z-10">
                  <div className="bg-[#FF6B35]/20 p-4 rounded-2xl text-[#FF6B35] group-hover:bg-[#FF6B35] group-hover:text-white transition-all shadow-sm border border-[#FF6B35]/20">
                    <FileText size={24} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-white text-base mb-2 leading-tight group-hover:text-[#FFC107] transition-colors drop-shadow-sm">{doc.title}</h4>
                    <div className="flex items-center gap-3 text-xs text-slate-300 font-semibold">
                      <span className="glass-modern !bg-white/10 px-3 py-1 rounded-md text-white border border-white/10">{doc.type}</span>
                      <span>{doc.size}</span>
                    </div>
                  </div>
                  <button className="text-slate-400 hover:text-[#0A192F] transition-colors p-2 glass-modern !bg-white/5 rounded-full group-hover:!bg-[#FFC107] group-hover:border-transparent shadow-sm">
                    <Download size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center relative z-10">
            <button className="glass-modern !bg-transparent text-white px-10 py-4 rounded-2xl font-bold transition-all shadow-[0_8px_20px_rgba(0,0,0,0.2)] hover:shadow-[0_12px_30px_rgba(0,0,0,0.4)] hover:-translate-y-1 inline-flex items-center gap-2 text-lg border border-white/40 hover:bg-white/10">
              Ver todos los documentos <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* Footer Institucional Premium */}
      <footer className="bg-[#050B14] text-slate-400 py-20 border-t-[8px] border-[#FFC107] relative overflow-hidden">
        <div className="absolute top-0 right-1/3 w-96 h-96 bg-[#FF6B35] rounded-full filter blur-[250px] opacity-10 pointer-events-none"></div>
        <div className="absolute inset-0 stripes-dark opacity-10 pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-12 gap-12 lg:gap-16">
            
            {/* Columna 1 - Info */}
            <div className="md:col-span-5">
              <div className="flex items-center gap-4 mb-8 group cursor-pointer">
                <div className="h-14 w-14 rounded-2xl border-2 border-[#FFC107]/50 bg-gradient-to-br from-[#0A192F] to-[#143058] flex items-center justify-center relative overflow-hidden">
                  <Building size={20} className="text-[#FFC107] absolute bottom-2 left-2 opacity-60" />
                  <PlaneTakeoff size={24} className="text-white relative z-20 absolute top-2 right-1.5" />
                </div>
                <span className="font-bold text-white text-xl leading-tight uppercase tracking-widest">Junta Comunal<br/><span className="text-[#00B4D8] text-lg">Tocumen</span></span>
              </div>
              <p className="text-sm md:text-base text-slate-400 mb-8 leading-relaxed max-w-sm font-light">
                Trabajando día a día por el bienestar, la seguridad y el progreso continuo de nuestra comunidad y su gente.
              </p>
              <div className="flex gap-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-10 w-10 rounded-full glass-modern !bg-white/5 border border-white/10 flex items-center justify-center hover:!bg-[#FF6B35] hover:text-white hover:border-transparent cursor-pointer transition-all hover:-translate-y-1 shadow-sm">
                    <div className="w-4 h-4 bg-current rounded-sm opacity-80"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Columna 2 - Links */}
            <div className="md:col-span-3">
              <h4 className="text-white font-bold mb-8 uppercase tracking-widest text-sm border-l-2 border-[#FF6B35] pl-4 drop-shadow">Enlaces Rápidos</h4>
              <ul className="space-y-4 text-sm font-medium">
                <li><a href="#inicio" className="hover:text-[#FFC107] transition-colors flex items-center gap-2"><ChevronRight size={14} className="text-[#FF6B35]"/> Inicio</a></li>
                <li><a href="#nosotros" className="hover:text-[#FFC107] transition-colors flex items-center gap-2"><ChevronRight size={14} className="text-[#FF6B35]"/> Sobre Nosotros</a></li>
                <li><a href="#tramites" className="hover:text-[#FFC107] transition-colors flex items-center gap-2"><ChevronRight size={14} className="text-[#FF6B35]"/> Solicitudes</a></li>
                <li><a href="#emprendedores" className="hover:text-[#FFC107] transition-colors flex items-center gap-2"><ChevronRight size={14} className="text-[#FF6B35]"/> Directorio Local</a></li>
                <li><a href="#transparencia" className="hover:text-[#FFC107] transition-colors flex items-center gap-2"><ChevronRight size={14} className="text-[#FF6B35]"/> Transparencia</a></li>
              </ul>
            </div>

            {/* Columna 3 - Contacto */}
            <div className="md:col-span-4" id="contacto">
              <h4 className="text-white font-bold mb-8 uppercase tracking-widest text-sm border-l-2 border-[#FF6B35] pl-4 drop-shadow">Contáctenos</h4>
              <ul className="space-y-5 text-sm">
                <li className="flex items-start gap-4 group">
                  <div className="glass-modern !bg-white/5 p-2 rounded-xl text-[#FFC107] mt-0.5 group-hover:!bg-[#FF6B35] group-hover:text-white transition-colors border border-white/10"><MapPin size={18} /></div>
                  <span className="leading-relaxed text-slate-300 font-light mt-1">Junta Comunal de Tocumen,<br/>Vía Principal, Sede Central.</span>
                </li>
                <li className="flex items-center gap-4 group">
                  <div className="glass-modern !bg-white/5 p-2 rounded-xl text-[#FFC107] group-hover:!bg-[#FF6B35] group-hover:text-white transition-colors border border-white/10"><Phone size={18} /></div>
                  <span className="text-slate-300 font-semibold text-base">295-0000 / 295-0001</span>
                </li>
                <li className="flex items-center gap-4 group">
                  <div className="glass-modern !bg-white/5 p-2 rounded-xl text-[#FFC107] group-hover:!bg-[#FF6B35] group-hover:text-white transition-colors border border-white/10"><Mail size={18} /></div>
                  <span className="break-all text-slate-300 font-medium">info@jctocumen.gob.pa</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-white/10 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500 font-medium relative z-10">
            <p>&copy; 2026 Junta Comunal de Tocumen. Todos los derechos reservados.</p>
            <p className="flex items-center gap-2 glass-modern !bg-[#0A192F]/50 px-4 py-2 rounded-full border border-white/10">
              Representante: <span className="text-white font-bold tracking-wide">H.R. Arielis J. Barría</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}